import React, { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import findKey from 'lodash/findKey';
import flatMap from 'lodash/flatMap';
import { BreadcrumbSection, BreadcrumbDivider, Breadcrumb, Table } from 'semantic-ui-react';
import HasRight from 'nallian-shared-ui/lib/layout/rights/HasRight';
import { Loader, Tab, Flex, Link, Icon, Button } from 'nallian-shared-ui/lib/components';
import { useNavigate } from 'react-router-dom';
import communityAppService from '../../services/communityApps/communityAppService';
import {
  FEATURE_TABS,
  ACTIONS,
  COMMUNITY_DETAIL_TABS,
  COMMUNITY_TAB_ACTIONS,
} from '../../constants';
import FeatureRoles from '../../../apps/components/feature/FeatureRoles';
import FeatureDetails from '../../../apps/components/feature/FeatureDetails';
import RoleDetails from '../../../apps/components/role/RoleDetails';
import FeatureRolesTable from '../../../apps/components/feature/FeatureRolesTable';
import EditCommunityApp from './EditCommunityApp';

const TAB_INDEX_MAPPING = {
  [FEATURE_TABS.DETAILS]: 0,
  [FEATURE_TABS.ROLES]: 1,
};

const SubviewFlex = styled(Flex)`
  && .tab {
    box-shadow: none;
    border: none;
    padding-left: 0.2rem;
    padding-right: 0;
  }
`;

const CommunityAppFeatures = ({ dispatchCommunityAppGet, communityAppFetch }) => {
  const { id, action, actionId, view, viewId, viewPane } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (actionId) dispatchCommunityAppGet(actionId, undefined, t);
  }, [dispatchCommunityAppGet, actionId, t]);

  // map this data first since we use values from both the community app and the app installation to show
  const communityAppFeatures = useMemo(
    () =>
      (communityAppFetch.value?.communityAppFeatures || []).map(caf => ({
        ...caf,
        tag: caf.appInstallationFeature.tag,
        name: caf.appInstallationFeature.name,
        description: caf.appInstallationFeature.description,
        isGlobal: caf.appInstallationFeature.isGlobal,
        communityAppFeatureRoles: caf.communityAppFeatureRoles.map(cafr => ({
          ...cafr,
          tag: cafr.appInstallationFeatureRole?.tag,
          name: cafr.appInstallationFeatureRole?.name,
          isObsolete: cafr.appInstallationFeatureRole?.isObsolete,
          description: cafr.appInstallationFeatureRole?.description,
        })),
      })),
    [communityAppFetch.value]
  );

  const { feature, role } = useMemo(() => {
    const flattenedData = flatMap(communityAppFeatures, f =>
      (f.communityAppFeatureRoles || []).map(r => ({ ...r, feature: f }))
    );

    if (view === 'features') {
      return {
        feature: flattenedData.find(f => f.feature.id === viewId)?.feature,
      };
    }
    if (view === 'roles') {
      const r = flattenedData.find(fr => fr.id === viewId);
      return {
        feature: r?.feature,
        role: r,
      };
    }
    return {};
  }, [view, viewId, communityAppFeatures]);

  return (
    <Flex fluid direction="column">
      <EditCommunityApp
        open={id && action === COMMUNITY_TAB_ACTIONS.EDIT_APP && actionId}
        returnRoute={`/communities/${ACTIONS.EDIT}/${id}/${COMMUNITY_DETAIL_TABS.APPS}/${COMMUNITY_TAB_ACTIONS.VIEW_APP}/${actionId}`}
      />
      <Flex fluid justifyContent="space-between">
        <Breadcrumb size="big">
          <BreadcrumbSection>
            <Link to={`/communities/${ACTIONS.EDIT}/${id}/${COMMUNITY_DETAIL_TABS.APPS}`}>
              <strong>{t('apps_plural')}</strong>
            </Link>
          </BreadcrumbSection>
          <BreadcrumbDivider icon="right chevron" />
          <BreadcrumbSection>
            <Link
              to={`/communities/${ACTIONS.EDIT}/${id}/${COMMUNITY_DETAIL_TABS.APPS}/${COMMUNITY_TAB_ACTIONS.VIEW_APP}/${actionId}`}
            >
              <strong>{communityAppFetch.value?.appInstallation?.name}</strong>
            </Link>
          </BreadcrumbSection>
          {role ? (
            <>
              <BreadcrumbDivider icon="right chevron" />
              <BreadcrumbSection>
                <Link
                  to={`/communities/${ACTIONS.EDIT}/${id}/${COMMUNITY_DETAIL_TABS.APPS}/${COMMUNITY_TAB_ACTIONS.VIEW_APP}/${actionId}/features/${feature?.id}`}
                >
                  <strong>{feature?.name}</strong>
                </Link>
              </BreadcrumbSection>
              <BreadcrumbDivider icon="right chevron" />
              <BreadcrumbSection>
                <strong>{role?.name}</strong>
              </BreadcrumbSection>
            </>
          ) : feature ? (
            <>
              <BreadcrumbDivider icon="right chevron" />
              <BreadcrumbSection>
                <strong>{feature?.name}</strong>
              </BreadcrumbSection>
            </>
          ) : (
            undefined
          )}
        </Breadcrumb>
        <HasRight right="COMMUNITYAPPS.MANAGE.ALL">
          <Link
            to={`/communities/${ACTIONS.EDIT}/${id}/${COMMUNITY_DETAIL_TABS.APPS}/${COMMUNITY_TAB_ACTIONS.EDIT_APP}/${actionId}`}
          >
            <Button linkbutton icon="pencil" content={t('edit')} />
          </Link>
        </HasRight>
      </Flex>
      {viewId ? (
        <SubviewFlex fluid direction="column" margin={feature ? '0.8rem 0 0' : '0'}>
          <Tab
            menu={{ secondary: true, pointing: true }}
            activeIndex={TAB_INDEX_MAPPING[viewPane]}
            onTabChange={(e, { activeIndex }) => {
              const activeTab = findKey(TAB_INDEX_MAPPING, index => index === activeIndex);
              if (activeTab) {
                navigate(
                  `/communities/${ACTIONS.EDIT}/${id}/${COMMUNITY_DETAIL_TABS.APPS}/${COMMUNITY_TAB_ACTIONS.VIEW_APP}/${actionId}/${view}/${viewId}/${activeTab}`
                );
              }
            }}
            panes={[
              {
                menuItem: t('details'),
                render: () => (
                  <Tab.Pane>
                    {view === 'features' ? (
                      <FeatureDetails feature={feature} isLoading={communityAppFetch.pending} />
                    ) : (
                      <RoleDetails role={role} isLoading={communityAppFetch.pending} />
                    )}
                  </Tab.Pane>
                ),
              },
              view === 'features'
                ? {
                    menuItem: t('roles'),
                    render: () => (
                      <Tab.Pane>
                        <FeatureRolesTable
                          rootUrl={`/communities/${ACTIONS.EDIT}/${id}/${COMMUNITY_DETAIL_TABS.APPS}/${COMMUNITY_TAB_ACTIONS.VIEW_APP}/${actionId}`}
                          roles={feature?.communityAppFeatureRoles}
                          hideDelete
                        />
                      </Tab.Pane>
                    ),
                  }
                : undefined,
            ]}
          />
        </SubviewFlex>
      ) : (
        <Table basic="very">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width="4">{t('feature')}</Table.HeaderCell>
              <Table.HeaderCell width="2">{t('isGlobal')}</Table.HeaderCell>
              <Table.HeaderCell width="2">{t('requiresSubscription')}</Table.HeaderCell>
              <Table.HeaderCell width="8">{t('roles')}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {communityAppFeatures.map(f => (
              <Table.Row key={`feature_${f.id}`}>
                <Table.Cell width="4">
                  <Link
                    to={`/communities/${ACTIONS.EDIT}/${id}/${COMMUNITY_DETAIL_TABS.APPS}/${COMMUNITY_TAB_ACTIONS.VIEW_APP}/${actionId}/features/${f.id}/${FEATURE_TABS.DETAILS}`}
                  >
                    {f.name}
                    {f.alias && ` (${f.alias})`}
                  </Link>
                </Table.Cell>
                <Table.Cell width="2">
                  <Icon
                    name={f.isGlobal ? 'checkmark' : 'minus'}
                    color={f.isGlobal ? 'green' : 'gray'}
                  />
                </Table.Cell>
                <Table.Cell width="2">
                  <Icon
                    name={f.requiresSubscription ? 'checkmark' : 'minus'}
                    color={f.requiresSubscription ? 'green' : 'gray'}
                  />
                </Table.Cell>
                <Table.Cell width="8">
                  <FeatureRoles
                    rootUrl={`/communities/${ACTIONS.EDIT}/${id}/${COMMUNITY_DETAIL_TABS.APPS}/${COMMUNITY_TAB_ACTIONS.VIEW_APP}/${actionId}`}
                    value={f.communityAppFeatureRoles}
                  />
                </Table.Cell>
              </Table.Row>
            ))}
            {communityAppFetch.pending && (
              <Flex fluid justifyContent="center">
                <Loader inline active />
              </Flex>
            )}
            {!communityAppFetch.pending && !communityAppFetch.value?.communityAppFeatures?.length && (
              <Table.Row>
                <Table.Cell>{t('noFeaturesYet')}</Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      )}
    </Flex>
  );
};

CommunityAppFeatures.propTypes = {
  dispatchCommunityAppGet: PropTypes.func.isRequired,
  communityAppFetch: PropTypes.object.isRequired,
};

export default communityAppService(CommunityAppFeatures);
